/**
 * Background layers for the Landing page, including top marquee text.
 */
import { motion } from "framer-motion"
import React, { useEffect, useMemo, useRef, useState } from "react"
import LandingBackgroundStadiumLights from "./LandingBackgroundStadiumLights"

// ---------------------------------------------------------------
// Small utility hook: true if the media query currently matches.
// ---------------------------------------------------------------
function useMediaQuery(query: string) {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window !== "undefined" && "matchMedia" in window) {
      return window.matchMedia(query).matches
    }
    return false
  })

  useEffect(() => {
    if (typeof window === "undefined" || !("matchMedia" in window)) return

    const media = window.matchMedia(query)
    const listener = () => setMatches(media.matches)
    media.addEventListener("change", listener)
    return () => media.removeEventListener("change", listener)
  }, [query])

  return matches
}

// Define a type for triangle elements
type Triangle = { id: string; isUp: boolean; x: number; y: number; delay: number }

const LandingBackground = React.memo(function LandingBackground() {
  // Cycle state to orchestrate flash → caret → pause sequence
  const [cycle, setCycle] = useState(0) // start at 0 so flashes and clusters render immediately
  // Seed for ambient single caret pops between main cycles
  const [ambientSeed, setAmbientSeed] = useState(0)
  // Seed to trigger massive trading burst when music starts
  const [burstSeed, setBurstSeed] = useState(0)
  // Track page visibility to pause background effects when tab is inactive
  const [isPageVisible, setIsPageVisible] = useState<boolean>(() => {
    if (typeof document === "undefined") return true
    return !document.hidden
  })
  useEffect(() => {
    if (typeof document === "undefined") return
    const handleVisibility = () => {
      setIsPageVisible(!document.hidden)
    }
    document.addEventListener("visibilitychange", handleVisibility)
    return () => document.removeEventListener("visibilitychange", handleVisibility)
  }, [])

  // Overlay lights displayed by default; stadium lights fade in later
  const [showStadiumLightsState, setShowStadiumLightsState] = useState(false)
  const [stadiumOpacity, setStadiumOpacity] = useState(0.125)
  const [_overlayOpacity, setOverlayOpacity] = useState(0.75)

  // -------------------------------------------------------------------
  // Central brightness control — tweak this single value to adjust how
  // intense the stadium lights appear across the entire landing page.
  // A value of `1` means 100 % brightness, `0.5` is half, `1.2` is 20 %
  // brighter, etc.
  // -------------------------------------------------------------------
  // const _DEFAULT_LIGHT_BRIGHTNESS = 0.2

  // Ref for ambient interval; cycle is now event-driven
  const ambientIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // -------------------------------------------------------------------
  // Helper: debounce hiding of stadium lights so a queued fade-out
  // timeout doesn't mistakenly run after the user quickly scrolls away
  // from the hero section (e.g. by pressing the Space bar).
  // -------------------------------------------------------------------
  const hideLightsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Cancel any pending hide-lights timeout
  const cancelHideLightsTimeout = () => {
    if (hideLightsTimeoutRef.current) {
      clearTimeout(hideLightsTimeoutRef.current)
      hideLightsTimeoutRef.current = null
    }
  }

  // Schedule the stadium lights to be unmounted after the fade-out completes
  const scheduleHideLights = () => {
    cancelHideLightsTimeout()
    hideLightsTimeoutRef.current = setTimeout(() => {
      setShowStadiumLightsState(false)
      hideLightsTimeoutRef.current = null
    }, 500)
  }

  // Track whether the Bracket section is currently visible.
  const isBracketVisibleRef = useRef(false)
  const pendingBurstRef = useRef(false)

  // Listen for visibility changes of the Bracket logo/section.
  useEffect(() => {
    const handleBracketVisibility = (evt: Event) => {
      const customEvt = evt as CustomEvent
      const isVisible = Boolean(customEvt.detail?.isVisible)
      isBracketVisibleRef.current = isVisible

      // If a burst was requested while the section was off-screen, fire it now.
      if (isVisible && pendingBurstRef.current) {
        setBurstSeed((s) => s + 1)
        pendingBurstRef.current = false
      }
    }

    window.addEventListener("bracketLogoVisibility", handleBracketVisibility as EventListener)
    return () => window.removeEventListener("bracketLogoVisibility", handleBracketVisibility as EventListener)
  }, [])

  // Listen for music playing mode to potentially trigger a burst. If the Bracket
  // section isn't visible yet we mark it as pending and wait for visibility.
  useEffect(() => {
    const handleMusicMode = (evt: Event) => {
      const customEvt = evt as CustomEvent
      if (customEvt.detail?.isPlaying) {
        if (isBracketVisibleRef.current) {
          setBurstSeed((s) => s + 1)
        } else {
          pendingBurstRef.current = true
        }
      }
    }

    window.addEventListener("musicPlayingMode", handleMusicMode as EventListener)
    return () => window.removeEventListener("musicPlayingMode", handleMusicMode as EventListener)
  }, [])

  // Listen for sports emoji clicks to trigger camera flash and caret effects
  useEffect(() => {
    const handleSportsEmojiClick = () => {
      // Trigger a single camera flash and caret cluster instantly
      setCycle((c) => c + 1)
      triggerSportsBurst()
    }

    window.addEventListener("sportsEmojiClick", handleSportsEmojiClick)
    return () => window.removeEventListener("sportsEmojiClick", handleSportsEmojiClick)
  }, [])

  // Track arc visibility state
  const [isArcVisible, setIsArcVisible] = useState(false)

  // Track demo section visibility to pause background effects
  const [isDemoSectionVisible, setIsDemoSectionVisible] = useState(false)

  // Track hero visibility so we can disable bursts after the user scrolls away
  const [isHeroVisible, setIsHeroVisible] = useState(true)

  // Enable bursts for a short window after a sports emoji is clicked in the Bracket section
  const [sportsBurstActive, setSportsBurstActive] = useState(false)

  // -------------------------------------------------------------------
  // Suppress lights-on logic when user clicks the Bracket logo (nav-logo scroll)
  // -------------------------------------------------------------------
  const skipLightsRef = useRef(false)
  useEffect(() => {
    const handleNavLogoScroll = () => {
      skipLightsRef.current = true
      // Clear the flag after ~1.4s (covers smooth scroll duration)
      setTimeout(() => {
        skipLightsRef.current = false
      }, 1400)
    }
    window.addEventListener("navLogoScrollTop", handleNavLogoScroll)
    return () => window.removeEventListener("navLogoScrollTop", handleNavLogoScroll)
  }, [])

  // Helper to start / stop the temporary sports burst window
  const triggerSportsBurst = () => {
    setSportsBurstActive(true)
    // Auto-disable after 5 s to avoid infinite bursts once music stops
    setTimeout(() => setSportsBurstActive(false), 5000)
  }

  // Bursts are active only when the hero is visible and the demo section is not
  const isBurstActive = isPageVisible && (isHeroVisible || sportsBurstActive) && !isDemoSectionVisible
  // Ambient carets are only active when the page is visible
  const isAmbientActive = isPageVisible

  // Listen for arc visibility to control stadium lights
  useEffect(() => {
    const handleArcVisibility = (evt: Event) => {
      const customEvt = evt as CustomEvent
      const isVisible = Boolean(customEvt.detail?.isVisible)
      setIsArcVisible(isVisible)

      if (isVisible) {
        // Arc is visible - turn on stadium lights
        cancelHideLightsTimeout()
        setShowStadiumLightsState(true)
        setStadiumOpacity(0.325)
        setOverlayOpacity(0.75)
      } else {
        // Arc is not visible - turn off stadium lights
        setStadiumOpacity(0)
        setOverlayOpacity(0)
        scheduleHideLights()

        // Dispatch reset event for arc and other components
        window.dispatchEvent(new CustomEvent("landingSequenceReset", { detail: { fromArc: true } }))
      }
    }

    window.addEventListener("landingArcVisible", handleArcVisibility as EventListener)
    return () => window.removeEventListener("landingArcVisible", handleArcVisibility as EventListener)
  }, [])

  // Simplified scroll position listener for sections after arc
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY
      const windowHeight = window.innerHeight

      // Only manage sections that come after the arc
      const askBrackyStart = windowHeight * 3.5 // Start of ask bracky section
      const bracketSectionStart = windowHeight * 6 // Approximate start of bracket section

      // Only adjust lights if arc is visible (lights are already on)
      if (isArcVisible) {
        if (scrollY >= askBrackyStart && scrollY < bracketSectionStart) {
          // From Ask @bracky downward – keep lights mounted but at MAX blur
          // setLightBrightness(DEFAULT_LIGHT_BRIGHTNESS) // Removed dynamic brightness
        } else if (scrollY >= bracketSectionStart) {
          // Bracket section - lights ON full (keep environment bright + brighter)
          // setLightBrightness(DEFAULT_LIGHT_BRIGHTNESS) // Removed dynamic brightness
        }
      }
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [isArcVisible])

  // Listen for hero visibility to control blend mode
  useEffect(() => {
    const heroHandler = (evt: Event) => {
      const custom = evt as CustomEvent
      const visible = Boolean(custom.detail?.isVisible)
      setIsHeroVisible(visible)
      if (visible) {
        // Fade out lights when back to hero and reset sequence
        setStadiumOpacity(0)
        setOverlayOpacity(0)
        // Hide the stadium lights after the fade transition completes
        scheduleHideLights()

        // Also dispatch reset event for consistency
        window.dispatchEvent(new CustomEvent("landingSequenceReset", { detail: { fromHero: true } }))
      }
    }

    const featureHandler = (evt: Event) => {
      if (skipLightsRef.current) return // ignore during nav-logo scroll
      const custom = evt as CustomEvent
      const visible = Boolean(custom.detail?.isVisible)
      if (visible) {
        cancelHideLightsTimeout()
        setShowStadiumLightsState(true)
        setStadiumOpacity(0.325)
        setOverlayOpacity(0.75)
      }
      // Don't turn off lights here - let the scroll handler manage that
      // to prevent flickering when transitioning between sections
    }

    const askHandler = (evt: Event) => {
      if (skipLightsRef.current) return // ignore during nav-logo scroll
      const custom = evt as CustomEvent
      const visible = Boolean(custom.detail?.isVisible)
      if (visible) {
        cancelHideLightsTimeout()
        setShowStadiumLightsState(true)
        setStadiumOpacity(0.325)
        setOverlayOpacity(0.75)
        // Constant blur
      }
    }

    const bracketHandler = (evt: Event) => {
      if (skipLightsRef.current) return // ignore during nav-logo scroll
      const custom = evt as CustomEvent
      const visible = Boolean(custom.detail?.isVisible)
      if (visible) {
        // Bracket section in view – ensure lights are bright
        cancelHideLightsTimeout()
        setShowStadiumLightsState(true)
        setOverlayOpacity(0.75)
        setStadiumOpacity(0.325)
        // setLightBrightness(DEFAULT_LIGHT_BRIGHTNESS) // Removed dynamic brightness
      }
    }

    const demoSectionHandler = (evt: Event) => {
      const custom = evt as CustomEvent
      const visible = Boolean(custom.detail?.isVisible)
      setIsDemoSectionVisible(visible)
    }

    // Use passive listeners for better performance
    window.addEventListener("landingHeroVisible", heroHandler, { passive: true })
    window.addEventListener("brackyFeaturesVisible", featureHandler, { passive: true })
    window.addEventListener("askBrackyVisible", askHandler, { passive: true })
    window.addEventListener("bracketSectionVisible", bracketHandler, { passive: true })
    window.addEventListener("landingDemoSectionVisible", demoSectionHandler, { passive: true })

    return () => {
      window.removeEventListener("landingHeroVisible", heroHandler)
      window.removeEventListener("brackyFeaturesVisible", featureHandler)
      window.removeEventListener("askBrackyVisible", askHandler)
      window.removeEventListener("bracketSectionVisible", bracketHandler)
      window.removeEventListener("landingDemoSectionVisible", demoSectionHandler)
    }
  }, [])

  // -------------------------------------------------------------------
  // Tweaks for camera flashes that PRECEDE the caret clusters
  // -------------------------------------------------------------------
  // Increase size +20% and brightness.  Adjust these numbers to taste.
  // const _FLASH_SIZE_BASE = 80 * 1.2 // previously 80
  // const _FLASH_SIZE_VARIATION = 120 * 1.2 // previously 120
  const FLASH_GRADIENT_ALPHA = 0.72 // previously 0.52
  // Warmer orange-ish tone for camera flashes (R,G,B)
  const FLASH_COLOR = "255,248,240" // #FFF8F0 - tweak to taste
  // -------------------------------------------------------------------
  // Tweaks for the trading caret triangles
  // -------------------------------------------------------------------
  // Apply overall 0.9 scale to shrink caret size by 10%
  const CARET_SCALE = 0.9
  const CARET_BORDER_BASE = 8 * CARET_SCALE // px (half-width)
  const CARET_HEIGHT_BASE = 14 * CARET_SCALE // px (height)
  const CARET_DESKTOP_MULTIPLIER = 1.55 // carets increased 15% to further enlarge carets on ≥640 px screens

  // Increase blur by 15%
  const CARET_BLUR = 4 * 0.8 * 1.15 // previous value 3.2 px → 3.68 px

  // Is the viewport at least Tailwind's `sm` breakpoint (640px)?
  const isDesktop = useMediaQuery("(min-width: 640px)")
  const caretBorderW = isDesktop ? CARET_BORDER_BASE * CARET_DESKTOP_MULTIPLIER : CARET_BORDER_BASE
  const caretBorderH = isDesktop ? CARET_HEIGHT_BASE * CARET_DESKTOP_MULTIPLIER : CARET_HEIGHT_BASE

  // Adjust blur: reduce by 25% on mobile
  const caretBlur = isDesktop ? CARET_BLUR : CARET_BLUR * 0.62

  // Constant blur for stadium lights - unused but kept for reference
  // const LIGHT_BLUR = window.innerWidth >= 1280 ? 4.8 : window.innerWidth >= 768 ? 5.4 : isDesktop ? 6 : 3
  // const _lightFilterVal = `blur(${LIGHT_BLUR}px) brightness(${DEFAULT_LIGHT_BRIGHTNESS})`

  // Generate camera flashes for current cycle
  const flashes = useMemo(() => {
    // COMMENTED OUT: Camera flashes disabled for now
    return [] as Array<{ id: string; x: number; y: number; size: number; delay: number }>
    // if (cycle < 0 || !isBurstActive) return [] // not started yet or bursts disabled
    // const count = 4
    // return Array.from({ length: count }).map((_, i) => ({
    //   id: `${cycle}-${i}`,
    //   x: Math.random() * 100,
    //   y: Math.random() * 100,
    //   size: FLASH_SIZE_BASE + Math.random() * FLASH_SIZE_VARIATION,
    //   delay: Math.random() * 0.8,
    // }))
  }, [cycle, isBurstActive])

  const [triangles, setTriangles] = useState<Triangle[]>([])
  // Persistent ambient carets so they aren't replaced mid-animation
  const [ambientTriangles, setAmbientTriangles] = useState<Triangle[]>([])

  // Clear any queued/active triangles when page is hidden
  useEffect(() => {
    if (!isPageVisible) {
      setTriangles([])
      setAmbientTriangles([])
    }
  }, [isPageVisible])

  // Whenever a new burst of flashes is generated, append corresponding triangles
  useEffect(() => {
    if (!isBurstActive) return

    const newTriangles: Triangle[] = flashes.flatMap((f) => {
      const clusterSize = 3 + Math.floor(Math.random() * 3) // 3–5 arrows
      const offset = () => (Math.random() - 0.5) * 6 // ±3vh/vw
      return Array.from({ length: clusterSize }).map((_, idx) => ({
        id: `${f.id}-${idx}`,
        isUp: Math.random() < 0.6,
        x: f.x + offset(),
        y: f.y + offset(),
        delay: f.delay + 0.6 + Math.random() * 0.4,
      }))
    })

    if (newTriangles.length) {
      setTriangles((prev) => [...prev, ...newTriangles])

      // NOTE: Per-triangle cleanup now handled via onAnimationComplete to allow
      // each caret to fade out organically at its own pace.
    }
  }, [flashes, isBurstActive])

  // Camera flash flickers (large blurry flashes)
  const lights = useMemo(() => {
    const count = 0
    return Array.from({ length: count }).map(
      (_, i): { id: number; x: number; y: number; size: number; delay: number; duration: number; pause: number } => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 20, // keep them toward top half
        size: 100 + Math.random() * 12, // px
        delay: Math.random() * 4,
        duration: 0.2 + Math.random() * 0.4,
        pause: 10 + Math.random() * 3,
      }),
    )
  }, [])

  // --- AMBIENT CARETS ---
  // Random caret triangles to simulate quiet trading even when no major "bursts" are happening
  const AMBIENT_CARET_COUNT = 12
  const ambientFlashes = useMemo(() => {
    if (ambientSeed === 0 || !isAmbientActive) return []
    return Array.from({ length: AMBIENT_CARET_COUNT }).map((_, i) => ({
      id: `ambient-${ambientSeed}-${i}`,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 2,
    }))
  }, [ambientSeed, isAmbientActive])

  // Music-triggered corner camera flashes
  const musicFlashes = useMemo(() => {
    // COMMENTED OUT: Music flashes disabled for now
    return [] as Array<{ id: string; x: number; y: number; size: number; delay: number }>
    // if (burstSeed === 0 || !isBurstActive) return []
    // // 4 main corners ranges
    // const positions = [
    //   { xRange: [2, 18], yRange: [2, 18] }, // TL
    //   { xRange: [82, 98], yRange: [2, 18] }, // TR
    //   { xRange: [2, 18], yRange: [82, 98] }, // BL
    //   { xRange: [82, 98], yRange: [82, 98] }, // BR
    // ]
    // return positions.map((pos, i) => ({
    //   id: `music-flash-${burstSeed}-${i}`,
    //   x: pos.xRange[0] + Math.random() * (pos.xRange[1] - pos.xRange[0]),
    //   y: pos.yRange[0] + Math.random() * (pos.yRange[1] - pos.yRange[0]),
    //   size: 70 + Math.random() * 60, // same ballpark as ambient flashes
    //   delay: Math.random() * 0.15,
    // }))
  }, [burstSeed, isBurstActive])

  // Camera flash bursts triggered by music (replaces music burst caret clusters)
  const burstLights = useMemo(() => {
    // COMMENTED OUT: Burst lights disabled for now
    return [] as Array<{ id: string; x: number; y: number; size: number; delay: number; duration: number }>
    // if (burstSeed === 0 || !isBurstActive) return [] // only generate when music is playing and bursts are enabled

    // const edgeCount = 4 // surrounding centre region
    // const centerCount = 4 // tightly clustered at true centre

    // const edgeLights = Array.from({ length: edgeCount }).map((_, i) => ({
    //   id: `burst-light-${burstSeed}-edge-${i}`,
    //   x: 30 + Math.random() * 40, // 30–70 vw
    //   y: 20 + Math.random() * 50, // 20–70 vh
    //   size: 110 + Math.random() * 90, // px
    //   delay: Math.random() * 0.25,
    //   duration: 0.8 + Math.random() * 0.4,
    // }))

    // const centerLights = Array.from({ length: centerCount }).map((_, i) => ({
    //   id: `burst-light-${burstSeed}-center-${i}`,
    //   x: 45 + (Math.random() - 0.5) * 10, // 40–50 vw (tight centre)
    //   y: 40 + (Math.random() - 0.5) * 10, // 35–45 vh
    //   size: 130 + Math.random() * 70, // slightly bigger
    //   delay: 0.05 + Math.random() * 0.15,
    //   duration: 0.9 + Math.random() * 0.3,
    // }))

    // return [...edgeLights, ...centerLights]
  }, [burstSeed, isBurstActive])

  // --- AMBIENT EFFECTS ---
  // Ambient carets spawn at a fixed interval unless the page is hidden
  useEffect(() => {
    if (!isAmbientActive) return

    const startAmbientInterval = () => {
      if (typeof document !== "undefined" && document.hidden) return
      ambientIntervalRef.current = setInterval(() => {
        setAmbientSeed((s) => s + 1)
      }, 12000)
    }

    const stopAmbientInterval = () => {
      if (ambientIntervalRef.current) {
        clearInterval(ambientIntervalRef.current)
        ambientIntervalRef.current = null
      }
    }

    // Start the ambient interval
    startAmbientInterval()

    // Pause ambient effects when page is hidden
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopAmbientInterval()
      } else {
        startAmbientInterval()
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      stopAmbientInterval()
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [isAmbientActive])

  // Pause ambient effects when user interacts with sports emojis
  useEffect(() => {
    const pauseAmbient = () => {
      if (ambientIntervalRef.current) {
        clearInterval(ambientIntervalRef.current)
        ambientIntervalRef.current = null
      }
    }

    const resumeAmbient = () => {
      if (typeof document !== "undefined" && document.hidden) return
      if (!ambientIntervalRef.current) {
        ambientIntervalRef.current = setInterval(() => {
          setAmbientSeed((s) => s + 1)
        }, 12000)
      }
    }

    const handleSportsEmojiClick = () => {
      pauseAmbient()
      setTimeout(resumeAmbient, 8000)
    }

    window.addEventListener("sportsEmojiClick", handleSportsEmojiClick)
    return () => window.removeEventListener("sportsEmojiClick", handleSportsEmojiClick)
  }, [])

  // Create ambient caret triangles
  useEffect(() => {
    if (!isAmbientActive) return

    const newAmbientTriangles: Triangle[] = ambientFlashes.map((f) => ({
      id: `ambient-triangle-${f.id}`,
      isUp: Math.random() < 0.6,
      x: f.x,
      y: f.y,
      delay: f.delay,
    }))

    if (newAmbientTriangles.length) {
      setAmbientTriangles((prev) => [...prev, ...newAmbientTriangles])

      // Ambient triangles will also self-remove after their animation completes.
    }
  }, [ambientFlashes, isAmbientActive])

  // Fire shorter event for music-triggered bursts
  useEffect(() => {
    if (burstSeed === 0) return

    window.dispatchEvent(new CustomEvent("tradeActivity", { detail: { active: true } }))

    const offTimer = setTimeout(() => {
      window.dispatchEvent(new CustomEvent("tradeActivity", { detail: { active: false } }))
    }, 3000)

    return () => clearTimeout(offTimer)
  }, [burstSeed])

  // Notify logo of ambient trading activity
  useEffect(() => {
    if (ambientSeed === 0) return

    window.dispatchEvent(new CustomEvent("ambientTrade", { detail: { active: true } }))

    const offTimer = setTimeout(() => {
      window.dispatchEvent(new CustomEvent("ambientTrade", { detail: { active: false } }))
    }, 2000)

    return () => clearTimeout(offTimer)
  }, [ambientSeed])

  // NEW: Broadcast stadium lights state so other components can listen
  // This should sync exactly with stadium lights visibility (both mount state AND opacity)
  useEffect(() => {
    const isLightsActuallyVisible = showStadiumLightsState && stadiumOpacity > 0
    window.dispatchEvent(
      new CustomEvent("stadiumLightsChanged", {
        detail: {
          on: isLightsActuallyVisible,
          opacity: stadiumOpacity,
        },
      }),
    )
  }, [showStadiumLightsState, stadiumOpacity])

  // -------------------------------------------------------------------
  // Guarantee stadium lights turn off when user scrolls to top of page
  // -------------------------------------------------------------------
  useEffect(() => {
    const handleScrollTop = () => {
      // If the user is at (or very near) the top of the page, make sure
      // the lights are faded out and eventually unmounted. We check for a
      // small threshold rather than strict 0 to account for mobile rubber-band
      // scrolling and sub-pixel values.
      if (window.scrollY <= 2) {
        cancelHideLightsTimeout()
        // Trigger fade-out
        setStadiumOpacity(0)
        setOverlayOpacity(0)
        // Schedule unmount after the fade completes
        scheduleHideLights()
      }
    }

    // Run once on mount in case the page loads at the very top
    handleScrollTop()

    window.addEventListener("scroll", handleScrollTop, { passive: true })
    return () => window.removeEventListener("scroll", handleScrollTop)
  }, [])

  return (
    <div className="overflow-x-hidden">
      {/* Global scanlines overlay – sits above all background effects */}
      <div className="fixed inset-0 pointer-events-none scanlines opacity-[0.1] mix-blend-overlay z-10" />

      {/* Base layer */}
      <div className="fixed inset-0 bg-linear-to-t from-[#030307] to-[#06081A] mix-blend-overlay z-0" />
      {/* Orbs removed */}

      {/* Simplified stadium lights */}
      <LandingBackgroundStadiumLights />

      {/* Camera flashes */}
      {flashes.map((f) => (
        <motion.div
          key={f.id}
          className="pointer-events-none fixed z-60 scale-[.6] sm:scale-100"
          style={{
            top: `${f.y}vh`,
            left: `${f.x}vw`,
            width: `${f.size}px`,
            height: `${f.size}px`,
            borderRadius: "50%",
            background: `radial-gradient(circle, rgba(${FLASH_COLOR},${FLASH_GRADIENT_ALPHA}) 0%, rgba(${FLASH_COLOR},0) 80%)`,
            filter: isBurstActive ? "blur(45px)" : "none",
          }}
          initial={{ opacity: 0, scale: 0.2 }}
          animate={{ opacity: [0, 1, 0], scale: [0.2, 1.2, 1.8] }}
          transition={{ duration: 0.8, delay: f.delay }}
        />
      ))}

      {/* Blue blurred circle fixed to top */}
      <div
        className="fixed top-[-250px] left-1/2 transform -translate-x-1/2 w-[50vw] h-[328px] rounded-full blur-[95px] pointer-events-none z-0"
        style={{
          background:
            "radial-gradient(circle, #001CFF 0%, rgba(0, 28, 255, 0.6) 40%, rgba(0, 28, 255, 0.2) 70%, transparent 100%)",
          maxWidth: "100vw",
        }}
      />

      {/* Primary gradient at top (scrolls with page) */}
      <div className="absolute top-0 left-0 w-full h-[147px] bg-linear-to-b from-[#001CFF] to-[#001CFF]/0 pointer-events-none z-0" />

      {/* CRT scanline overlay ONLY ON TOP BLUE HEADER (fades out toward the bottom) */}
      <div
        className="fixed top-0 left-0 w-full h-36 sm:h-48 pointer-events-none z-0 scanlines opacity-[.35] mix-blend-overlay"
        style={{
          WebkitMaskImage: "linear-gradient(to bottom, black 85%, transparent 100%)",
          maskImage: "linear-gradient(to bottom, black 85%, transparent 100%)",
        }}
      />

      {/* Camera flash flickers */}
      {lights.map((l) => (
        <motion.div
          // eslint-disable-next-line react/no-array-index-key
          key={`light-${l.id}`}
          className="pointer-events-none fixed z-60 scale-[.65] sm:scale-100"
          style={{
            top: `${l.y}vh`,
            left: `${l.x}vw`,
            width: `${l.size}px`,
            height: `${l.size}px`,
            background: `radial-gradient(circle, rgba(${FLASH_COLOR},0.9) 0%, rgba(${FLASH_COLOR},0.6) 40%, rgba(${FLASH_COLOR},0.2) 70%, transparent 100%)`,
            borderRadius: "50%",
            filter: isBurstActive ? "blur(45px)" : "none",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0] }}
          transition={{
            duration: l.duration,
            delay: l.delay,
            repeat: Number.POSITIVE_INFINITY,
            repeatDelay: l.pause,
          }}
        />
      ))}

      {/* Camera flashes when music kicks in */}
      {musicFlashes.map((f) => (
        <motion.div
          key={f.id}
          className="pointer-events-none fixed z-60 scale-[.6] sm:scale-100"
          style={{
            top: `${f.y}vh`,
            left: `${f.x}vw`,
            width: `${f.size}px`,
            height: `${f.size}px`,
            borderRadius: "50%",
            background: `radial-gradient(circle, rgba(${FLASH_COLOR},0.9) 0%, rgba(${FLASH_COLOR},0) 70%)`,
            filter: isBurstActive ? "blur(45px)" : "none",
          }}
          initial={{ opacity: 0, scale: 0.2 }}
          animate={{ opacity: [0, 1, 0], scale: [0.2, 1.2, 1.8] }}
          transition={{ duration: 0.7, delay: f.delay }}
        />
      ))}

      {/* Animated trading caret triangles */}
      {triangles.map((t) => (
        <motion.div
          key={t.id}
          className="pointer-events-none fixed z-0"
          style={{
            top: `${t.y}vh`,
            left: `${t.x}vw`,
            width: 0,
            height: 0,
            borderLeft: `${caretBorderW}px solid transparent`,
            borderRight: `${caretBorderW}px solid transparent`,
            ...(t.isUp
              ? { borderBottom: `${caretBorderH}px solid #00FF51` }
              : { borderTop: `${caretBorderH}px solid #FF3B30` }),
            filter: `blur(${caretBlur}px)`,
          }}
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{
            opacity: [0, 0.75, 0.75, 0],
            scale: [0.5, 1, 1, 0.5],
            // Swap origin / motion: buy (green, pointing up) now rises from below baseline,
            // and sell (red, pointing down) falls from above, then returns.
            y: t.isUp ? [6, 0, 0, 6] : [-6, 0, 0, -6],
          }}
          transition={{ duration: 3, delay: t.delay }}
          onAnimationComplete={() => setTriangles((prev) => prev.filter((tr) => tr.id !== t.id))}
        />
      ))}

      {/* Music burst camera flash lights */}
      {burstLights.map((l) => (
        <motion.div
          key={l.id}
          className="pointer-events-none fixed z-60 scale-[.5] translate-x-[-20%] translate-y-[-30%] sm:scale-100 sm:translate-x-0 sm:translate-y-0"
          style={{
            top: `${l.y}vh`,
            left: `${l.x}vw`,
            width: `${l.size}px`,
            height: `${l.size}px`,
            background: `radial-gradient(circle, rgba(${FLASH_COLOR},0.9) 0%, rgba(${FLASH_COLOR},0.6) 40%, rgba(${FLASH_COLOR},0.2) 70%, transparent 100%)`,
            borderRadius: "50%",
            filter: isBurstActive ? "blur(12px)" : "none",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.6, 0] }}
          transition={{ duration: l.duration, delay: l.delay }}
        />
      ))}

      {/* Ambient random trading carets between big moments */}
      {ambientTriangles.map((t) => (
        <motion.div
          key={t.id}
          className="pointer-events-none fixed z-0"
          style={{
            top: `${t.y}vh`,
            left: `${t.x}vw`,
            width: 0,
            height: 0,
            borderLeft: `${caretBorderW}px solid transparent`,
            borderRight: `${caretBorderW}px solid transparent`,
            ...(t.isUp
              ? { borderBottom: `${caretBorderH}px solid #00FF51` }
              : { borderTop: `${caretBorderH}px solid #FF3B30` }),
            filter: `blur(${caretBlur}px)`,
          }}
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{
            opacity: [0, 0.5, 0.5, 0],
            scale: [0.5, 1, 1, 0.5],
            // Swap origin / motion: buy (green, pointing up) now rises from below baseline,
            // and sell (red, pointing down) falls from above, then returns.
            y: t.isUp ? [6, 0, 0, 6] : [-6, 0, 0, -6],
          }}
          transition={{ duration: 3, delay: t.delay }}
          onAnimationComplete={() => setAmbientTriangles((prev) => prev.filter((tr) => tr.id !== t.id))}
        />
      ))}
    </div>
  )
})

export default LandingBackground
